<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { usePolicy } from 'dashboard/composables/usePolicy';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import CaptainPaywall from 'dashboard/components-next/captain/pageComponents/Paywall.vue';
import CustomToolsPageEmptyState from 'dashboard/components-next/captain/pageComponents/emptyStates/CustomToolsPageEmptyState.vue';
import CreateCustomToolDialog from 'dashboard/components-next/captain/pageComponents/customTool/CreateCustomToolDialog.vue';
import CustomToolCard from 'dashboard/components-next/captain/pageComponents/customTool/CustomToolCard.vue';
import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';

const store = useStore();
const { isFeatureFlagEnabled, shouldShowPaywall } = usePolicy();

const SOFT_LIMIT = 10;
const isV2 = computed(() => isFeatureFlagEnabled(FEATURE_FLAGS.CAPTAIN_V2));

const uiFlags = useMapGetter('captainCustomTools/getUIFlags');
const customTools = useMapGetter('captainCustomTools/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);
const customToolsMeta = useMapGetter('captainCustomTools/getMeta');

const showSoftLimitWarning = computed(
  () => !isV2.value && customToolsMeta.value.totalCount > SOFT_LIMIT
);

const createDialogRef = ref(null);
const deleteDialogRef = ref(null);
const selectedTool = ref(null);
const dialogType = ref('');

const fetchCustomTools = (page = 1) => {
  store.dispatch('captainCustomTools/get', { page });
};

const onPageChange = page => fetchCustomTools(page);

const openCreateDialog = () => {
  dialogType.value = 'create';
  selectedTool.value = null;
  nextTick(() => createDialogRef.value.dialogRef.open());
};

const handleEdit = tool => {
  dialogType.value = 'edit';
  selectedTool.value = tool;
  nextTick(() => createDialogRef.value.dialogRef.open());
};

const handleDelete = tool => {
  selectedTool.value = tool;
  nextTick(() => deleteDialogRef.value.dialogRef.open());
};

const handleAction = ({ action, id }) => {
  const tool = customTools.value.find(t => t.id === id);
  if (action === 'edit') {
    handleEdit(tool);
  } else if (action === 'delete') {
    handleDelete(tool);
  }
};

const handleDialogClose = () => {
  dialogType.value = '';
  selectedTool.value = null;
};

const onDeleteSuccess = () => {
  selectedTool.value = null;
  // Check if page will be empty after deletion
  if (customTools.value.length === 1 && customToolsMeta.value.page > 1) {
    // Go to previous page if current page will be empty
    onPageChange(customToolsMeta.value.page - 1);
  } else {
    // Refresh current page
    fetchCustomTools(customToolsMeta.value.page);
  }
};

const cetakersHook = computed(() => {
  const integration = store.getters['integrations/getIntegration']('cetakers_flow');
  return integration?.hooks?.[0] || null;
});

const isPageEmpty = computed(() => !customTools.value.length && !cetakersHook.value);

onMounted(() => {
  store.dispatch('integrations/get');
  if (!shouldShowPaywall(FEATURE_FLAGS.CAPTAIN_CUSTOM_TOOLS)) {
    fetchCustomTools();
  }
});
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.CUSTOM_TOOLS.HEADER')"
    :button-label="$t('CAPTAIN.CUSTOM_TOOLS.ADD_NEW')"
    :button-policy="['administrator']"
    :feature-flag="FEATURE_FLAGS.CAPTAIN_CUSTOM_TOOLS"
    :total-count="customToolsMeta.totalCount"
    :current-page="customToolsMeta.page"
    :show-pagination-footer="!isFetching && !!customTools.length"
    :is-fetching="isFetching"
    :is-empty="isPageEmpty"
    :show-know-more="false"
    @update:current-page="onPageChange"
    @click="openCreateDialog"
  >
    <template #paywall>
      <CaptainPaywall feature-prefix="CAPTAIN.CUSTOM_TOOLS" />
    </template>

    <template #emptyState>
      <CustomToolsPageEmptyState @click="openCreateDialog" />
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <!-- Cetakers Flow Native Integration Tools Card -->
        <div class="outline outline-n-container outline-1 bg-n-card rounded-xl p-5 border border-n-weak">
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-3">
              <img :src="'/dashboard/images/integrations/cetakers_flow.png'" alt="Cetakers Flow" class="size-10 rounded-xl border border-n-weak p-1 bg-n-alpha-3 dark:bg-n-alpha-2 shadow-xs shrink-0" />
              <div>
                <h4 class="text-sm font-bold text-n-slate-12 flex items-center gap-2">
                  Cetakers Flow Native Tools
                  <span class="px-2 py-0.5 text-[10px] font-bold rounded-full" :class="cetakersHook ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-n-alpha-2 text-n-slate-11'">
                    {{ cetakersHook ? 'HYBRID NATIVE TOOLS' : 'TEMPLATE INTEGRASI' }}
                  </span>
                </h4>
                <p class="text-xs text-n-slate-11">
                  Tools otomatis dari Cetakers Flow (SPK, Produk, Invoice, Resi, Pelanggan) langsung aktif secara hybrid tanpa perlu isi URL endpoint manual.
                </p>
              </div>
            </div>
            <router-link
              :to="{ name: 'settings_integrations_cetakers_flow' }"
              class="px-3.5 py-2 text-xs font-semibold text-w-brand-primary bg-w-brand-primary/10 rounded-lg hover:bg-w-brand-primary/20 transition-colors shrink-0"
            >
              {{ cetakersHook ? 'Atur Izin & Status Modul' : 'Sambungkan Cetakers Flow' }}
            </router-link>
          </div>

          <div v-if="cetakersHook" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 pt-2">
            <div class="p-2.5 rounded-lg border border-n-weak bg-n-alpha-1 flex items-center justify-between text-xs">
              <span class="font-semibold text-n-slate-12 flex items-center gap-2">
                <span class="i-lucide-file-text size-4 text-blue-600"></span> Data SPK & Order
              </span>
              <span class="text-[10px] font-bold px-2 py-0.5 rounded-full" :class="cetakersHook?.settings?.captain_modules?.spk !== false ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-slate-100 text-slate-500'">
                {{ cetakersHook?.settings?.captain_modules?.spk !== false ? 'AKTIF' : 'NONAKTIF' }}
              </span>
            </div>

            <div class="p-2.5 rounded-lg border border-n-weak bg-n-alpha-1 flex items-center justify-between text-xs">
              <span class="font-semibold text-n-slate-12 flex items-center gap-2">
                <span class="i-lucide-shopping-bag size-4 text-indigo-600"></span> Master Produk & Harga
              </span>
              <span class="text-[10px] font-bold px-2 py-0.5 rounded-full" :class="cetakersHook?.settings?.captain_modules?.products !== false ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-slate-100 text-slate-500'">
                {{ cetakersHook?.settings?.captain_modules?.products !== false ? 'AKTIF' : 'NONAKTIF' }}
              </span>
            </div>

            <div class="p-2.5 rounded-lg border border-n-weak bg-n-alpha-1 flex items-center justify-between text-xs">
              <span class="font-semibold text-n-slate-12 flex items-center gap-2">
                <span class="i-lucide-credit-card size-4 text-emerald-600"></span> Data Invoice & Tagihan
              </span>
              <span class="text-[10px] font-bold px-2 py-0.5 rounded-full" :class="cetakersHook?.settings?.captain_modules?.invoices !== false ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-slate-100 text-slate-500'">
                {{ cetakersHook?.settings?.captain_modules?.invoices !== false ? 'AKTIF' : 'NONAKTIF' }}
              </span>
            </div>

            <div class="p-2.5 rounded-lg border border-n-weak bg-n-alpha-1 flex items-center justify-between text-xs">
              <span class="font-semibold text-n-slate-12 flex items-center gap-2">
                <span class="i-lucide-truck size-4 text-amber-600"></span> Pengiriman & Resi
              </span>
              <span class="text-[10px] font-bold px-2 py-0.5 rounded-full" :class="cetakersHook?.settings?.captain_modules?.shipping !== false ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-slate-100 text-slate-500'">
                {{ cetakersHook?.settings?.captain_modules?.shipping !== false ? 'AKTIF' : 'NONAKTIF' }}
              </span>
            </div>

            <div class="p-2.5 rounded-lg border border-n-weak bg-n-alpha-1 flex items-center justify-between text-xs sm:col-span-2">
              <span class="font-semibold text-n-slate-12 flex items-center gap-2">
                <span class="i-lucide-users size-4 text-purple-600"></span> Data Master Pelanggan
              </span>
              <span class="text-[10px] font-bold px-2 py-0.5 rounded-full" :class="cetakersHook?.settings?.captain_modules?.customers !== false ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-slate-100 text-slate-500'">
                {{ cetakersHook?.settings?.captain_modules?.customers !== false ? 'AKTIF' : 'NONAKTIF' }}
              </span>
            </div>
          </div>
        </div>

        <div
          v-if="showSoftLimitWarning"
          class="flex items-center gap-2 px-4 py-3 text-sm rounded-lg bg-n-amber-2 text-n-amber-11"
        >
          <span class="i-lucide-triangle-alert size-4 shrink-0" />
          {{ $t('CAPTAIN.CUSTOM_TOOLS.SOFT_LIMIT_WARNING') }}
        </div>
        <CustomToolCard
          v-for="tool in customTools"
          :id="tool.id"
          :key="tool.id"
          :title="tool.title"
          :description="tool.description"
          :endpoint-url="tool.endpoint_url"
          :http-method="tool.http_method"
          :auth-type="tool.auth_type"
          :param-schema="tool.param_schema"
          :enabled="tool.enabled"
          :created-at="tool.created_at"
          :updated-at="tool.updated_at"
          @action="handleAction"
        />
      </div>
    </template>
  </PageLayout>

  <CreateCustomToolDialog
    v-if="dialogType"
    ref="createDialogRef"
    :type="dialogType"
    :selected-tool="selectedTool"
    @close="handleDialogClose"
  />

  <DeleteDialog
    v-if="selectedTool"
    ref="deleteDialogRef"
    :entity="selectedTool"
    type="CustomTools"
    translation-key="CUSTOM_TOOLS"
    @delete-success="onDeleteSuccess"
  />
</template>
